/* ═══════════════════════════════════════════════════════════════════════════
   screen-capture-ui.js  ·  PHASE 3 — user-facing page
   Vanilla JS, Shopify-embedded, matches EOH frontend conventions:
   fetch(API + '/screen-capture/...', { headers:{ Authorization:'Bearer '+token }}).
   Opened via a button. Form → screen share → capture → review/delete → generate.
   Three usage meters (images / documents / saved files) shown live.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  // API base — mirror the app's resolution.
  var _isDevEnv = (
    window.location.pathname.indexOf('/pages/erp-chat_dev') !== -1 ||
    window.location.pathname.indexOf('_dev') !== -1 ||
    window.location.search.indexOf('preview_theme_id=159788171482') !== -1
  );
  var API = (window.location.hostname === 'erponlinehub.com' && !_isDevEnv)
    ? 'https://erp-ai-backend.vercel.app'
    : 'https://erp-ai-backend-dev.vercel.app';

  function token() {
    if (window.S && window.S.token) return window.S.token;
    try { var t = localStorage.getItem('eoh_t'); if (t) return t; } catch (e) {}
    return window.__EOH_TOKEN || '';
  }
  function authHeaders(json) {
    var h = { 'Authorization': 'Bearer ' + token() };
    if (json) h['Content-Type'] = 'application/json';
    return h;
  }
  function api(path, opts) {
    opts = opts || {};
    opts.headers = Object.assign(authHeaders(opts.body && typeof opts.body === 'string'), opts.headers || {});
    return window.fetch(API + '/screen-capture' + path, opts).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, status: r.status, data: j }; });
    });
  }

  // ── state ──
  var ST = {
    config: null,
    shots: [],          // captured-but-unsaved {id, url, order}
    stream: null,
    video: null,
    pip: null,          // Document Picture-in-Picture window (floating capture widget)
    fixedWidget: null,  // fallback fixed overlay
    open: false,
  };

  // ── styles (injected once) ──
  function injectStyle() {
    if (document.getElementById('sc-style')) return;
    var css = [
      '.sc-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:99999;display:flex;align-items:center;justify-content:center;font-family:var(--font,system-ui,Segoe UI,Roboto,sans-serif)}',
      '.sc-panel{background:var(--card,#fff);color:var(--text,#0f172a);border:1px solid var(--border,#eef1f5);border-radius:14px;width:min(920px,94vw);height:92vh;max-height:92vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.4)}',
      '.sc-head{padding:18px 22px;border-bottom:1px solid var(--border,#eef1f5);display:flex;align-items:center;justify-content:space-between;background:var(--card,#fff);flex-shrink:0}',
      '.sc-head h2{margin:0;font-size:18px;color:var(--text,#0f172a)}',
      '.sc-x{cursor:pointer;border:none;background:none;font-size:22px;color:var(--muted,#64748b);line-height:1}',
      '.sc-body{padding:20px 22px;overflow:auto;flex:1;min-height:0}',
      '.sc-msgbar-wrap{flex-shrink:0}',
      '.sc-meters{display:flex;gap:10px;margin-bottom:18px;flex-wrap:wrap}',
      '.sc-meter{flex:1;min-width:150px;background:var(--bg3,#f8fafc);border:1px solid var(--border,#eef1f5);border-radius:10px;padding:10px 12px}',
      '.sc-meter b{display:block;font-size:12px;color:var(--muted,#64748b);font-weight:600}',
      '.sc-meter .v{font-size:18px;font-weight:700;color:var(--text,#0f172a);margin-top:2px}',
      '.sc-meter .bar{height:5px;background:var(--bg4,#e2e8f0);border-radius:3px;margin-top:6px;overflow:hidden}',
      '.sc-meter .bar>i{display:block;height:100%;background:var(--g1,#2563eb)}',
      '.sc-field{margin-bottom:12px}',
      '.sc-field label{display:block;font-size:13px;color:var(--text,#334155);font-weight:600;margin-bottom:4px}',
      '.sc-field .req{color:#e11d48}',
      '.sc-field input,.sc-field select,.sc-field textarea{width:100%;padding:8px 10px;border:1px solid var(--border2,#cbd5e1);border-radius:8px;font-size:14px;box-sizing:border-box;background:var(--bg2,#fff);color:var(--text,#0f172a)}',
      '.sc-people-row{display:flex;gap:8px;margin-bottom:6px}',
      '.sc-btn{background:linear-gradient(135deg,var(--g1,#2563eb),var(--g2,#2563eb));color:#fff;border:none;border-radius:8px;padding:10px 16px;font-size:14px;font-weight:600;cursor:pointer}',
      '.sc-btn.ghost{background:transparent;color:var(--gl,#2563eb);border:1px solid var(--gl,#2563eb)}',
      '.sc-btn.danger{background:transparent;color:#e11d48;border:1px solid #e11d48}',
      '.sc-btn:disabled{opacity:.5;cursor:not-allowed}',
      '.sc-shots{display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:10px;margin-top:12px}',
      '.sc-shot{position:relative;border:1px solid var(--border,#e2e8f0);border-radius:8px;overflow:hidden;cursor:pointer;opacity:.55;transition:.15s}',
      '.sc-shot.sel{opacity:1;border-color:var(--g1,#2563eb);box-shadow:0 0 0 2px var(--g1,#2563eb)}',
      '.sc-shot img{width:100%;display:block;aspect-ratio:16/10;object-fit:cover;cursor:zoom-in}',
      '.sc-num{position:absolute;top:4px;left:4px;background:rgba(15,23,42,.85);color:#fff;font-size:11px;font-weight:700;min-width:20px;height:20px;border-radius:5px;display:flex;align-items:center;justify-content:center;z-index:2;padding:0 4px}',
      '.sc-check{position:absolute;bottom:4px;left:4px;background:var(--g1,#2563eb);color:#fff;font-size:12px;font-weight:700;width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;z-index:2}',
      '.sc-shot:not(.sel) .sc-check{background:rgba(120,120,120,.6)}',
      '.sc-shot .del{position:absolute;top:4px;right:4px;background:rgba(225,29,72,.92);color:#fff;border:none;border-radius:6px;width:24px;height:24px;cursor:pointer;font-size:13px}',
      '.sc-note{font-size:12px;color:var(--muted,#64748b);margin-top:6px}',
      '.sc-warn{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.3);color:#ef4444;border-radius:8px;padding:10px 12px;font-size:13px;margin:10px 0}',
      '.sc-q{background:var(--bg3,#f8fafc);border:1px solid var(--border,#eef1f5);border-radius:8px;padding:10px 12px;margin-bottom:8px}',
      '.sc-q p{margin:0 0 6px;font-size:13px;color:var(--text,#0f172a)}',
      '.sc-q .opts{display:flex;gap:6px;flex-wrap:wrap;align-items:center}',
      '.sc-q button{border:1px solid var(--border2,#cbd5e1);background:var(--bg2,#fff);color:var(--text,#0f172a);border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer}',
      '.sc-q button.sel{background:var(--g1,#2563eb);color:#fff;border-color:var(--g1,#2563eb)}',
      '.sc-actions{display:flex;gap:10px;justify-content:flex-end;margin-top:18px}',
    ].join('');
    var s = document.createElement('style'); s.id = 'sc-style'; s.textContent = css;
    document.head.appendChild(s);
  }

  function meter(label, used, cap) {
    var pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
    var over = cap > 0 && used >= cap;
    return '<div class="sc-meter"><b>' + label + '</b><div class="v">' + used + ' / ' + (cap || '∞') +
      '</div><div class="bar"><i style="width:' + pct + '%;background:' + (over ? '#e11d48' : '#2563eb') + '"></i></div></div>';
  }

  // ── open / render ──
  function open() {
    injectStyle();
    var ov = document.createElement('div'); ov.className = 'sc-overlay'; ov.id = 'sc-overlay';
    ov.innerHTML = '<div class="sc-panel"><div class="sc-head"><h2>Screen Capture Document</h2>' +
      '<div><button class="sc-btn ghost" id="sc-tab-new" style="padding:6px 12px">New</button> ' +
      '<button class="sc-btn ghost" id="sc-tab-images" style="padding:6px 12px">Images</button> ' +
      '<button class="sc-btn ghost" id="sc-tab-saved" style="padding:6px 12px">Files</button> ' +
      '<button class="sc-x" id="sc-close">×</button></div></div>' +
      '<div id="sc-msgbar" style="display:none"></div>' +
      '<div class="sc-body" id="sc-body">Loading…</div></div>';
    document.body.appendChild(ov);
    document.getElementById('sc-close').onclick = close;
    document.getElementById('sc-tab-new').onclick = function () { loadConfig(); };
    document.getElementById('sc-tab-images').onclick = renderImages;
    document.getElementById('sc-tab-saved').onclick = renderSaved;
    ST.open = true;
    loadConfig();
  }
  function close() {
    if (ST.stream) { try { ST.stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {} ST.stream = null; }
    closeFloatingWidget();
    var ov = document.getElementById('sc-overlay'); if (ov) ov.remove();
    ST.open = false; ST.shots = [];
  }

  function loadConfig() {
    api('/config').then(function (r) {
      if (!r.ok || !r.data.enabled) {
        document.getElementById('sc-body').innerHTML = '<div class="sc-warn">This feature isn\'t available on your plan.</div>';
        return;
      }
      ST.config = r.data;
      // Load the user's already-stored shots so the count reflects reality (not zero),
      // and the New tab shows what's already captured/stored.
      api('/shots').then(function (sr) {
        ST.shots = (sr.ok && sr.data.shots) ? sr.data.shots.map(function (s) {
          return { id: s.id, url: s.url, stored: true };
        }) : [];
        // Load saved presets (fast entry) — non-blocking.
        api('/presets').then(function (pr) {
          ST.presetsEnabled = !!(pr.ok && pr.data.enabled);
          ST.presets = (pr.ok && pr.data.presets) ? pr.data.presets : [];
          renderForm();
        }).catch(function () { ST.presetsEnabled = false; ST.presets = []; renderForm(); });
      }).catch(function () { ST.shots = []; renderForm(); });
    }).catch(function () {
      document.getElementById('sc-body').innerHTML = '<div class="sc-warn">Could not load. Please try again.</div>';
    });
  }

  // The true stored-image count = what's in the library (ST.shots reflects the server).
  function imageUsed() { return ST.shots.length; }
  function imageCap() { return (ST.config && ST.config.usage && ST.config.usage.images && ST.config.usage.images.cap) || 0; }

  function fieldVisible(key) {
    var f = (ST.config.fields || []).find(function (x) { return x.field_key === key; });
    return !f || f.is_visible !== false;
  }
  function fieldRequired(key) {
    var f = (ST.config.fields || []).find(function (x) { return x.field_key === key; });
    return f && f.is_required;
  }
  function reqMark(key) { return fieldRequired(key) ? ' <span class="req">*</span>' : ''; }

  function renderForm() {
    var u = ST.config.usage || {};
    var ft = ST.config.filetypes || [];
    var body = document.getElementById('sc-body');
    var html = '<div class="sc-meters" id="sc-meters">' +
      meter('Images', imageUsed(), imageCap()) +
      meter('Documents', (u.documents || {}).used || 0, (u.documents || {}).cap || 0) +
      meter('Saved Files', (u.saved || {}).used || 0, (u.saved || {}).cap || 0) +
      '</div>';

    // Saved presets bar (fast entry) — only if the admin enabled the feature.
    if (ST.presetsEnabled) {
      html += '<div class="sc-field" style="display:flex;gap:8px;align-items:end;flex-wrap:wrap;background:var(--bg3,#f8fafc);border:1px solid var(--border,#eef1f5);border-radius:10px;padding:12px">' +
        '<div style="flex:1;min-width:180px"><label style="display:block;font-size:12px;color:var(--muted);font-weight:600;margin-bottom:4px">Saved data (fast entry)</label>' +
        '<select id="sc-preset-load"><option value="">— Load a saved preset —</option></select></div>' +
        '<button class="sc-btn ghost" id="sc-preset-save" style="padding:8px 12px">💾 Save current</button>' +
        '<button class="sc-btn danger" id="sc-preset-del" style="padding:8px 12px" disabled>Delete</button>' +
        '</div>';
    }

    if (fieldVisible('project_name'))
      html += field('projectName', 'Project Name' + reqMark('project_name'), 'input');
    if (fieldVisible('company_name'))
      html += field('companyName', 'Company Name' + reqMark('company_name'), 'input');
    if (fieldVisible('company_logo'))
      html += '<div class="sc-field"><label>Company Logo' + reqMark('company_logo') + '</label>' +
        '<div style="display:flex;align-items:center;gap:10px">' +
        '<div id="sc-logo-preview" style="width:56px;height:56px;border:1px dashed var(--border2,#cbd5e1);border-radius:8px;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;font-size:10px;color:var(--muted,#94a3b8)">Logo</div>' +
        '<input id="sc-companyLogoFile" type="file" accept="image/png,image/jpeg" style="flex:1">' +
        '<button class="sc-btn ghost" id="sc-logo-clear" type="button" style="padding:6px 10px;font-size:12px">Clear</button>' +
        '</div>' +
        '<div style="display:flex;align-items:center;gap:8px;margin-top:8px">' +
        '<span class="sc-note" style="margin:0">Logo shape in the file:</span>' +
        '<button type="button" id="sc-logo-shape-rect" class="sc-shape-btn active" title="Rectangle (wide logos)" style="display:inline-flex;align-items:center;justify-content:center;width:40px;height:28px;border:1px solid var(--g1,#2563eb);border-radius:6px;background:transparent;cursor:pointer;padding:0"><span style="display:block;width:26px;height:14px;background:var(--muted,#94a3b8);border-radius:2px"></span></button>' +
        '<button type="button" id="sc-logo-shape-square" class="sc-shape-btn" title="Square (icon logos)" style="display:inline-flex;align-items:center;justify-content:center;width:34px;height:28px;border:1px solid var(--border2,#cbd5e1);border-radius:6px;background:transparent;cursor:pointer;padding:0"><span style="display:block;width:18px;height:18px;background:var(--muted,#94a3b8);border-radius:2px"></span></button>' +
        '</div>' +
        '<div class="sc-note">Shown in the document header. PNG/JPG, recommended small (under 1 MB). Pick <b>Rectangle</b> for wide/wordmark logos or <b>Square</b> for icon logos.</div></div>';
    if (fieldVisible('project_details'))
      html += field('projectDetails', 'Project Details' + reqMark('project_details'), 'textarea');
    if (fieldVisible('creator_name'))
      html += field('creatorName', 'Creator Name' + reqMark('creator_name'), 'input');
    if (fieldVisible('doc_date'))
      html += field('date', 'Date' + reqMark('doc_date'), 'date');
    if (fieldVisible('file_type')) {
      html += '<div class="sc-field"><label>File Type' + reqMark('file_type') + '</label><select id="sc-fileType">' +
        ft.map(function (o) { return '<option value="' + esc(o.label) + '">' + esc(o.label) + '</option>'; }).join('') +
        '<option value="__other">Other…</option></select>' +
        '<input id="sc-fileTypeOther" placeholder="Specify type" style="display:none;margin-top:6px"></div>';
    }
    if (fieldVisible('output_format'))
      html += '<div class="sc-field"><label>Output Format' + reqMark('output_format') + '</label>' +
        '<select id="sc-outputFormat"><option value="pdf">PDF</option><option value="docx">Word</option><option value="pptx">PowerPoint</option></select></div>';
    if (fieldVisible('people'))
      html += '<div class="sc-field"><label>People (name — job title)</label><div id="sc-people"></div>' +
        '<button class="sc-btn ghost" id="sc-addPerson" type="button" style="margin-top:6px;padding:6px 12px">+ Add person</button></div>';

    // capture area
    html += '<div style="margin-top:16px"><button class="sc-btn" id="sc-share">▶ Start screen sharing</button> ' +
      '<button class="sc-btn ghost" id="sc-capture" disabled>📸 Capture</button>' +
      '<span class="sc-note" id="sc-count"></span></div>' +
      '<div class="sc-note" style="margin-top:6px;line-height:1.5">💡 <b>To keep the capture popup out of your screenshots:</b> when the share dialog opens, pick the <b>single window or screen you want to document</b> (e.g. your SAP window) — not the one where this popup is floating. The popup stays on this browser’s screen, so sharing a different window/screen captures only your app, cleanly.</div>' +
      '<div style="display:flex;align-items:center;justify-content:space-between;margin-top:14px;flex-wrap:wrap;gap:8px">' +
      '<span class="sc-note" id="sc-sel-hint" style="margin:0"></span>' +
      '<span><button class="sc-btn ghost" id="sc-sel-all" style="padding:4px 10px;font-size:12px">Select all</button> ' +
      '<button class="sc-btn ghost" id="sc-sel-none" style="padding:4px 10px;font-size:12px">Select none</button></span></div>' +
      '<div class="sc-shots" id="sc-shotStrip"></div>' +
      '<div id="sc-clarify"></div>' +
      '<div class="sc-actions"><button class="sc-btn ghost" id="sc-cancel">Cancel</button>' +
      '<button class="sc-btn" id="sc-generate">Generate</button></div>';

    body.innerHTML = html;
    wireForm();
  }

  function field(id, label, type) {
    var input = type === 'textarea' ? '<textarea id="sc-' + id + '" rows="3"></textarea>'
      : '<input id="sc-' + id + '" type="' + (type === 'date' ? 'date' : 'text') + '">';
    return '<div class="sc-field"><label>' + label + '</label>' + input + '</div>';
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  function wireForm() {
    var ftSel = document.getElementById('sc-fileType');
    if (ftSel) ftSel.onchange = function () {
      document.getElementById('sc-fileTypeOther').style.display = ftSel.value === '__other' ? 'block' : 'none';
    };
    var addP = document.getElementById('sc-addPerson');
    if (addP) addP.onclick = function () { addPersonRow(); };
    var dt = document.getElementById('sc-date'); if (dt && !dt.value) dt.value = new Date().toISOString().slice(0, 10);

    // Company logo upload → data URL (kept in ST.companyLogo).
    var logoFile = document.getElementById('sc-companyLogoFile');
    if (logoFile) logoFile.onchange = function () {
      var f = logoFile.files && logoFile.files[0]; if (!f) return;
      // Only PNG/JPEG render reliably in the PDF/DOCX/PPTX builders (WebP/SVG/GIF fail).
      var okType = /image\/(png|jpe?g)/i.test(f.type);
      if (!okType) { showWarn('Please use a PNG or JPG logo (WebP, SVG and GIF are not supported in the files).'); logoFile.value = ''; return; }
      if (f.size > 2 * 1024 * 1024) { showWarn('Logo is too large (max ~2 MB).'); logoFile.value = ''; return; }
      var rd = new FileReader();
      rd.onload = function () {
        ST.companyLogo = rd.result;
        var pv = document.getElementById('sc-logo-preview');
        if (pv) pv.innerHTML = '<img src="' + rd.result + '" style="width:100%;height:100%;object-fit:contain">';
      };
      rd.readAsDataURL(f);
    };
    var logoClear = document.getElementById('sc-logo-clear');
    if (logoClear) logoClear.onclick = function () {
      ST.companyLogo = null;
      if (logoFile) logoFile.value = '';
      var pv = document.getElementById('sc-logo-preview'); if (pv) pv.textContent = 'Logo';
    };

    // Logo shape toggle (square / rectangle).
    if (!ST.logoShape) ST.logoShape = 'rect';
    var shRect = document.getElementById('sc-logo-shape-rect');
    var shSq = document.getElementById('sc-logo-shape-square');
    function setShape(shape) {
      ST.logoShape = shape;
      if (shRect) { shRect.style.borderColor = shape === 'rect' ? 'var(--g1,#2563eb)' : 'var(--border2,#cbd5e1)'; shRect.classList.toggle('active', shape === 'rect'); }
      if (shSq) { shSq.style.borderColor = shape === 'square' ? 'var(--g1,#2563eb)' : 'var(--border2,#cbd5e1)'; shSq.classList.toggle('active', shape === 'square'); }
    }
    if (shRect) shRect.onclick = function () { setShape('rect'); };
    if (shSq) shSq.onclick = function () { setShape('square'); };
    setShape(ST.logoShape);

    document.getElementById('sc-share').onclick = startShare;
    document.getElementById('sc-capture').onclick = captureFrame;
    document.getElementById('sc-cancel').onclick = close;
    document.getElementById('sc-generate').onclick = onGenerate;
    var selAll = document.getElementById('sc-sel-all');
    if (selAll) selAll.onclick = function () { ST.selected = {}; ST.shots.forEach(function (s) { ST.selected[s.id] = true; }); renderShots(); };
    var selNone = document.getElementById('sc-sel-none');
    if (selNone) selNone.onclick = function () { ST.selected = {}; ST.shots.forEach(function (s) { ST.selected[s.id] = false; }); renderShots(); };

    // ── Saved presets (fast entry) ──
    if (ST.presetsEnabled) {
      var loadSel = document.getElementById('sc-preset-load');
      var delBtn = document.getElementById('sc-preset-del');
      if (loadSel) {
        loadSel.innerHTML = '<option value="">— Load a saved preset —</option>' +
          (ST.presets || []).map(function (p) { return '<option value="' + p.id + '">' + esc(p.name) + '</option>'; }).join('');
        loadSel.onchange = function () {
          var p = (ST.presets || []).find(function (x) { return x.id === loadSel.value; });
          if (delBtn) delBtn.disabled = !p;
          if (p) applyPreset(p.data || {});
        };
      }
      var saveBtn = document.getElementById('sc-preset-save');
      if (saveBtn) saveBtn.onclick = function () {
        var name = prompt('Save these form values as a preset. Name it:', (val('sc-projectName') || 'My preset'));
        if (name == null) return;
        name = String(name).trim(); if (!name) { showWarn('Give the preset a name.'); return; }
        var data = gatherForm();
        delete data.companyLogo; // don't store the logo image in a preset (kept light)
        api('/presets', { method: 'POST', body: JSON.stringify({ name: name, data: data }) }).then(function (r) {
          if (!r.ok) { showWarn(r.data && r.data.message || 'Could not save preset.'); return; }
          // refresh the list
          api('/presets').then(function (pr) { ST.presets = (pr.ok && pr.data.presets) ? pr.data.presets : []; wireForm(); });
          showMsg('Preset "' + name + '" saved.');
        });
      };
      if (delBtn) delBtn.onclick = function () {
        var loadSel2 = document.getElementById('sc-preset-load');
        var id = loadSel2 && loadSel2.value; if (!id) return;
        if (!confirm('Delete this saved preset?')) return;
        api('/presets/' + id, { method: 'DELETE' }).then(function () {
          ST.presets = (ST.presets || []).filter(function (x) { return x.id !== id; });
          wireForm();
        });
      };
    }

    renderShots();
    updateCount();
  }

  // Fill the form fields from a saved preset's data.
  function applyPreset(d) {
    function set(id, v) { var e = document.getElementById(id); if (e && v != null) e.value = v; }
    set('sc-projectName', d.projectName);
    set('sc-companyName', d.companyName);
    set('sc-projectDetails', d.projectDetails);
    set('sc-creatorName', d.creatorName);
    set('sc-date', d.date);
    set('sc-outputFormat', d.outputFormat);
    // file type
    var ftSel = document.getElementById('sc-fileType');
    if (ftSel && d.fileType) {
      var found = Array.prototype.some.call(ftSel.options, function (o) { return o.value === d.fileType; });
      ftSel.value = found ? d.fileType : '__other';
      var other = document.getElementById('sc-fileTypeOther');
      if (other) { other.style.display = ftSel.value === '__other' ? 'block' : 'none'; if (ftSel.value === '__other') other.value = d.fileTypeOther || d.fileType || ''; }
    }
    // people
    var wrap = document.getElementById('sc-people');
    if (wrap && Array.isArray(d.people)) {
      wrap.innerHTML = '';
      d.people.forEach(function (p) { addPersonRow(p.name, p.title); });
    }
  }

  function addPersonRow(name, title) {
    var wrap = document.getElementById('sc-people'); if (!wrap) return;
    var row = document.createElement('div'); row.className = 'sc-people-row';
    row.innerHTML = '<input placeholder="Name" value="' + esc(name || '') + '"><input placeholder="Job title" value="' + esc(title || '') + '">' +
      '<button class="sc-btn ghost" type="button" style="padding:6px 10px">×</button>';
    row.querySelector('button').onclick = function () { row.remove(); };
    wrap.appendChild(row);
  }

  // ── screen share + capture ──
  function startShare() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      alert('Screen sharing is not supported in this browser.'); return;
    }
    navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: 30, displaySurface: 'window' },   // prefer Window in the picker
      // Keep our own tab/UI out of the capture surface where the browser supports it.
      selfBrowserSurface: 'exclude',
      surfaceSwitching: 'include',
      preferCurrentTab: false
    }).then(function (stream) {
      ST.stream = stream;
      var v = document.createElement('video'); v.autoplay = true; v.srcObject = stream; v.style.display = 'none';
      document.body.appendChild(v); ST.video = v;
      document.getElementById('sc-capture').disabled = false;
      document.getElementById('sc-share').textContent = '● Sharing (change)';
      openFloatingWidget();   // the on-top capture control
      stream.getVideoTracks()[0].onended = function () {
        document.getElementById('sc-capture').disabled = true;
        document.getElementById('sc-share').textContent = '▶ Start screen sharing';
        closeFloatingWidget();
      };
    }).catch(function () { /* user cancelled */ });
  }

  // ── Floating capture widget — stays visible ON TOP of the shared screen ──
  // Uses Document Picture-in-Picture (a true always-on-top window) when available,
  // else falls back to a fixed overlay pinned to the corner of the page.
  function openFloatingWidget() {
    var capTxt = imageCap() ? (' / ' + imageCap()) : '';
    var html =
      '<div id="scw-inner" style="font-family:system-ui,sans-serif;background:#0f172a;color:#fff;border-radius:12px;padding:14px;box-shadow:0 8px 30px rgba(0,0,0,.4);display:flex;flex-direction:column;gap:9px;min-width:200px;box-sizing:border-box">' +
      '<div style="font-size:12px;color:#94a3b8;font-weight:600">Screen Capture</div>' +
      '<div id="scw-count" style="font-size:22px;font-weight:700;line-height:1">' + imageUsed() + '<span style="font-size:13px;color:#94a3b8;font-weight:500">' + capTxt + ' images</span></div>' +
      '<div id="scw-bar" style="height:5px;background:#1e293b;border-radius:3px;overflow:hidden"><i id="scw-fill" style="display:block;height:100%;background:#2563eb;width:0%"></i></div>' +
      '<button id="scw-cap" style="background:#2563eb;color:#fff;border:none;border-radius:8px;padding:11px;font-size:14px;font-weight:600;cursor:pointer;width:100%">📸 Capture</button>' +
      '<button id="scw-stop" style="background:transparent;color:#f87171;border:1px solid #f8717155;border-radius:8px;padding:8px;font-size:12px;font-weight:600;cursor:pointer;width:100%">■ Stop sharing</button>' +
      '<div id="scw-msg" style="font-size:11px;color:#f87171;min-height:14px;text-align:center"></div>' +
      '</div>';

    // Floating Picture-in-Picture window — it floats OVER other apps (e.g. your SAP
    // window), which is the whole point. If PiP isn't supported, fall back to an
    // on-page overlay. We no longer try to auto-hide it; instead the page tells users
    // to share a single window/screen so the widget stays out of their shots.
    if (window.documentPictureInPicture && window.documentPictureInPicture.requestWindow) {
      window.documentPictureInPicture.requestWindow({ width: 300, height: 320 }).then(function (pip) {
        ST.pip = pip;
        pip.document.body.style.margin = '0';
        pip.document.body.style.background = 'transparent';
        pip.document.body.innerHTML = html;
        pip.document.getElementById('scw-cap').addEventListener('click', captureFrame);
        pip.document.getElementById('scw-stop').addEventListener('click', stopSharing);
        pip.addEventListener('pagehide', function () { ST.pip = null; });
        updateFloatingCount();
      }).catch(function () { fixedOverlayFallback(html); });
    } else {
      fixedOverlayFallback(html);
    }
  }

  function fixedOverlayFallback(html) {
    if (document.getElementById('scw-fixed')) return;
    var d = document.createElement('div');
    d.id = 'scw-fixed';
    d.style.cssText = 'position:fixed;top:16px;right:16px;z-index:2147483647';
    d.innerHTML = html;
    document.body.appendChild(d);
    d.querySelector('#scw-cap').addEventListener('click', captureFrame);
    var stopBtn = d.querySelector('#scw-stop');
    if (stopBtn) stopBtn.addEventListener('click', stopSharing);
    ST.fixedWidget = d;
    updateFloatingCount();
  }

  // Stop sharing — ends the stream, closes the widget, resets the share button.
  function stopSharing() {
    if (ST.stream) { try { ST.stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {} ST.stream = null; }
    ST.video = null;
    closeFloatingWidget();
    var capBtn = document.getElementById('sc-capture');
    if (capBtn) capBtn.disabled = true;
    var shareBtn = document.getElementById('sc-share');
    if (shareBtn) shareBtn.textContent = '▶ Start screen sharing';
  }

  function closeFloatingWidget() {
    try { if (ST.pip) { ST.pip.close(); ST.pip = null; } } catch (e) {}
    if (ST.fixedWidget) { ST.fixedWidget.remove(); ST.fixedWidget = null; }
  }

  function updateFloatingCount() {
    var used = imageUsed();
    var cap = imageCap();
    var pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
    var over = cap > 0 && used >= cap;
    [ST.pip && ST.pip.document, ST.fixedWidget].forEach(function (doc) {
      if (!doc) return;
      var root = doc.getElementById ? doc : doc;
      var cnt = root.querySelector ? root.querySelector('#scw-count') : null;
      var fill = root.querySelector ? root.querySelector('#scw-fill') : null;
      var cap2 = root.querySelector ? root.querySelector('#scw-cap') : null;
      if (cnt) cnt.innerHTML = used + '<span style="font-size:13px;color:#94a3b8">' + (cap ? ' / ' + cap : '') + ' images</span>';
      if (fill) { fill.style.width = pct + '%'; fill.style.background = over ? '#ef4444' : '#2563eb'; }
      if (cap2) { cap2.disabled = over; cap2.style.opacity = over ? '.5' : '1'; cap2.textContent = over ? 'Limit reached' : '📸 Capture'; }
    });
  }

  function captureFrame() {
    if (imageCap() > 0 && imageUsed() >= imageCap()) {
      showWarn('Image limit reached (' + imageCap() + '). Delete screenshots to make room.');
      floatingMsg('Limit reached — delete some to continue.'); return;
    }
    if (!ST.video) return;
    var dataUrl = null, _w = 0, _h = 0;
    try {
      _w = ST.video.videoWidth; _h = ST.video.videoHeight;
      var c = document.createElement('canvas');
      c.width = _w; c.height = _h;
      c.getContext('2d').drawImage(ST.video, 0, 0);
      dataUrl = c.toDataURL('image/png');
    } catch (e) { dataUrl = null; }
    if (!dataUrl) { showWarn('Could not capture the screen.'); return; }
    api('/shots', { method: 'POST', body: JSON.stringify({ image: dataUrl, width: _w, height: _h }) })
      .then(function (r) {
        if (!r.ok) { showWarn(r.data && r.data.message || 'Could not save screenshot.'); floatingMsg(r.data && r.data.message || 'Could not save.'); return; }
        ST.shots.push({ id: r.data.shot.id, url: dataUrl });
        renderShots(); updateCount(); updateFloatingCount(); refreshFormMeter();
        floatingFlash();
      });
  }

  function floatingMsg(t) {
    [ST.pip && ST.pip.document, ST.fixedWidget].forEach(function (doc) {
      if (!doc) return; var m = doc.querySelector ? doc.querySelector('#scw-msg') : null;
      if (m) { m.textContent = t; setTimeout(function () { if (m) m.textContent = ''; }, 4000); }
    });
  }
  function floatingFlash() {
    [ST.pip && ST.pip.document, ST.fixedWidget].forEach(function (doc) {
      if (!doc) return; var b = doc.querySelector ? doc.querySelector('#scw-cap') : null;
      if (b) { var o = b.style.background; b.style.background = '#059669'; b.textContent = '✓ Captured'; setTimeout(function () { if (b) { b.style.background = o; b.textContent = '📸 Capture'; } }, 500); }
    });
  }

  function renderShots() {
    var strip = document.getElementById('sc-shotStrip'); if (!strip) return;
    // Ensure a selection set exists; default = all selected.
    if (!ST.selected) ST.selected = {};
    // newly seen shots default to selected
    ST.shots.forEach(function (s) { if (ST.selected[s.id] === undefined) ST.selected[s.id] = true; });

    var selCount = ST.shots.filter(function (s) { return ST.selected[s.id]; }).length;
    var hint = document.getElementById('sc-sel-hint');
    if (hint) hint.textContent = selCount + ' of ' + ST.shots.length + ' selected for the document';

    strip.innerHTML = ST.shots.map(function (s, i) {
      var on = ST.selected[s.id];
      return '<div class="sc-shot' + (on ? ' sel' : '') + '" data-id="' + s.id + '">' +
        '<span class="sc-num">' + (i + 1) + '</span>' +
        '<span class="sc-check">' + (on ? '✓' : '') + '</span>' +
        '<img src="' + s.url + '" data-view="' + s.url + '" data-n="' + (i + 1) + '">' +
        '<button class="del" data-id="' + s.id + '" title="Delete">×</button>' +
        '</div>';
    }).join('');

    // toggle selection by clicking the tile (but not the image or delete)
    strip.querySelectorAll('.sc-shot').forEach(function (tile) {
      tile.onclick = function (e) {
        if (e.target.classList.contains('del') || e.target.tagName === 'IMG') return;
        var id = tile.getAttribute('data-id');
        ST.selected[id] = !ST.selected[id];
        renderShots();
      };
    });
    // click image → preview (lightbox)
    strip.querySelectorAll('img[data-view]').forEach(function (img) {
      img.onclick = function (e) { e.stopPropagation(); openLightbox(img.getAttribute('data-view'), img.getAttribute('data-n')); };
    });
    strip.querySelectorAll('.del').forEach(function (b) {
      b.onclick = function (e) { e.stopPropagation(); deleteShot(b.getAttribute('data-id')); };
    });
  }

  // Lightbox preview of a screenshot.
  function openLightbox(url, n) {
    var lb = document.createElement('div');
    lb.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:100001;display:flex;align-items:center;justify-content:center;cursor:zoom-out';
    lb.innerHTML = '<div style="position:absolute;top:16px;left:20px;color:#fff;font:600 14px system-ui">Screenshot ' + (n || '') + '</div>' +
      '<img src="' + url + '" style="max-width:94vw;max-height:90vh;border-radius:8px;box-shadow:0 10px 40px rgba(0,0,0,.5)">' +
      '<div style="position:absolute;top:16px;right:20px;color:#fff;font:700 26px system-ui;cursor:pointer">×</div>';
    lb.onclick = function () { lb.remove(); };
    document.body.appendChild(lb);
  }

  function deleteShot(id) {
    // delete BEFORE analysis — never sent to vision, frees a slot. Always allowed.
    api('/shots/' + id, { method: 'DELETE' }).then(function () {
      ST.shots = ST.shots.filter(function (s) { return s.id !== id; });
      renderShots(); updateCount(); updateFloatingCount(); refreshFormMeter();
    });
  }

  function updateCount() {
    var el = document.getElementById('sc-count'); if (!el) return;
    el.textContent = '  ' + imageUsed() + (imageCap() ? ' / ' + imageCap() : '') + ' images';
  }

  // Refresh the meters bar live (images/documents/saved) after capture/delete.
  function refreshFormMeter() { renderMeters(); }

  // Repaint ALL three meters from current ST.config.usage (used after generate/delete).
  function renderMeters() {
    var box = document.getElementById('sc-meters'); if (!box) return;
    var u = (ST.config && ST.config.usage) || {};
    box.innerHTML =
      meter('Images', imageUsed(), imageCap()) +
      meter('Documents', (u.documents || {}).used || 0, (u.documents || {}).cap || 0) +
      meter('Saved Files', (u.saved || {}).used || 0, (u.saved || {}).cap || 0);
  }

  // Header message bar — always visible at the top, even with many screenshots.
  // kind: 'error' | 'success' | 'info'
  function headerMsg(msg, kind, sticky) {
    var bar = document.getElementById('sc-msgbar'); if (!bar) return;
    var colors = {
      error:   { bg: '#fef2f2', bd: '#fecaca', fg: '#b91c1c' },
      success: { bg: '#f0fdf4', bd: '#bbf7d0', fg: '#15803d' },
      info:    { bg: '#eff6ff', bd: '#bfdbfe', fg: '#1d4ed8' }
    };
    var c = colors[kind] || colors.info;
    bar.style.cssText = 'flex-shrink:0;margin:12px 20px 0;padding:11px 14px;border-radius:10px;font-size:13px;font-weight:600;' +
      'background:' + c.bg + ';border:1px solid ' + c.bd + ';color:' + c.fg + ';display:flex;align-items:center;justify-content:space-between;gap:10px';
    bar.innerHTML = '<span>' + esc(msg) + '</span><span id="sc-msgbar-x" style="cursor:pointer;opacity:.6;font-weight:700">×</span>';
    var x = document.getElementById('sc-msgbar-x');
    if (x) x.onclick = function () { bar.style.display = 'none'; };
    if (bar._t) clearTimeout(bar._t);
    if (!sticky) bar._t = setTimeout(function () { bar.style.display = 'none'; }, 7000);
  }
  function showWarn(msg) { headerMsg(msg, 'error'); }
  function showMsg(msg, sticky) { headerMsg(msg, 'success', sticky); }
  function showInfo(msg, sticky) { headerMsg(msg, 'info', sticky); }

  // ── gather form ──
  function gatherForm() {
    var people = [];
    (document.querySelectorAll('#sc-people .sc-people-row') || []).forEach(function (r) {
      var ins = r.querySelectorAll('input');
      if (ins[0].value.trim() || ins[1].value.trim()) people.push({ name: ins[0].value.trim(), title: ins[1].value.trim() });
    });
    var ftSel = document.getElementById('sc-fileType');
    var fileType = ftSel ? (ftSel.value === '__other' ? '' : ftSel.value) : '';
    var fileTypeOther = ftSel && ftSel.value === '__other' ? (document.getElementById('sc-fileTypeOther').value || '') : '';
    return {
      projectName: val('sc-projectName'),
      companyName: val('sc-companyName'),
      companyLogo: ST.companyLogo || null,
      companyLogoShape: ST.logoShape || 'rect',
      projectDetails: val('sc-projectDetails'),
      creatorName: val('sc-creatorName'),
      date: val('sc-date'),
      fileType: fileType, fileTypeOther: fileTypeOther,
      outputFormat: val('sc-outputFormat') || 'pdf',
      people: people,
    };
  }
  function val(id) { var e = document.getElementById(id); return e ? e.value.trim() : ''; }

  // ── generate: clarify → questions → generate ──
  function selectedShotIds() {
    return ST.shots.filter(function (s) { return !ST.selected || ST.selected[s.id]; }).map(function (s) { return s.id; });
  }

  function onGenerate() {
    var form = gatherForm();
    if (!form.projectName) { showWarn('Project name is required.'); return; }
    var sel = selectedShotIds();
    if (!sel.length) { showWarn('Select at least one image to include in the document.'); return; }
    var gen = document.getElementById('sc-generate'); gen.disabled = true; gen.textContent = 'Reviewing…';

    api('/clarify', { method: 'POST', body: JSON.stringify({ form: form }) }).then(function (r) {
      if (r.data && r.data.blocked) { showWarn(r.data.message || 'Project name is required.'); gen.disabled = false; gen.textContent = 'Generate'; return; }
      var qs = (r.data && r.data.questions) || [];
      var emptyDesc = r.data && r.data.emptyDescription;
      if (qs.length || emptyDesc) { renderClarify(qs, emptyDesc, form); gen.disabled = false; gen.textContent = 'Generate'; }
      else { doGenerate(form, {}); }
    }).catch(function () { doGenerate(form, {}); });
  }

  function renderClarify(questions, emptyDesc, form) {
    var host = document.getElementById('sc-clarify'); host.innerHTML = '';
    if (emptyDesc) {
      var n = document.createElement('div'); n.className = 'sc-warn';
      n.textContent = 'The description is empty — did you want to add anything? You can proceed either way.';
      host.appendChild(n);
    }
    var answers = {};
    questions.forEach(function (q, i) {
      var box = document.createElement('div'); box.className = 'sc-q';
      box.innerHTML = '<p>' + esc(q) + '</p><div class="opts"><button data-a="Yes">Yes</button>' +
        '<button data-a="No">No</button><input placeholder="or type an answer" style="flex:1;padding:4px 8px;border:1px solid #cbd5e1;border-radius:6px"></div>';
      box.querySelectorAll('button').forEach(function (b) {
        b.onclick = function () {
          box.querySelectorAll('button').forEach(function (x) { x.classList.remove('sel'); });
          b.classList.add('sel'); answers[q] = b.getAttribute('data-a');
        };
      });
      box.querySelector('input').oninput = function (e) { answers[q] = e.target.value; };
      host.appendChild(box);
    });
    var go = document.createElement('button'); go.className = 'sc-btn'; go.textContent = 'Continue & Generate';
    go.style.marginTop = '8px';
    go.onclick = function () { doGenerate(form, answers); };
    host.appendChild(go);
  }

  function doGenerate(form, answers) {
    var gen = document.getElementById('sc-generate'); if (gen) { gen.disabled = true; gen.textContent = 'Generating…'; }
    var host = document.getElementById('sc-clarify');
    var ids = selectedShotIds();
    host.innerHTML = '<div class="sc-note">Analyzing ' + ids.length + ' screenshot(s) and building your file…</div>';
    api('/generate', { method: 'POST', body: JSON.stringify({ form: form, shotIds: ids, answers: answers }) })
      .then(function (r) {
        if (gen) { gen.disabled = false; gen.textContent = 'Generate'; }
        if (!r.ok) {
          var em = (r.data && r.data.message) || 'Generation failed.';
          if (r.data && r.data.detail) em += ' (' + r.data.detail + ')';
          showWarn(em);                                   // visible in the header bar
          host.innerHTML = '<div class="sc-warn">' + esc(em) + '</div>';
          scrollBodyTop();
          return;
        }
        var url = r.data.url;
        // Header success message — visible no matter how far down the user scrolled.
        showMsg('✅ Your document is ready and saved to Files' + (url ? ' — click Download.' : '.'), true);
        host.innerHTML = '<div class="sc-q"><p>✅ Document generated and saved to your Files.</p>' +
          (url ? '<a class="sc-btn" href="' + url + '" target="_blank" download>⬇ Download</a>' : '') +
          ' <button class="sc-btn ghost" id="sc-view-files">View Files</button></div>';
        var vf = document.getElementById('sc-view-files');
        if (vf) vf.onclick = renderSaved;
        scrollBodyTop();
        // NOTE: images are NOT cleared — they stay in your library (count against the
        // image cap) until you delete them. Refresh usage and repaint the meters live.
        api('/config').then(function (cr) {
          if (cr.ok && cr.data.enabled) { ST.config.usage = cr.data.usage; renderMeters(); }
        });
      });
  }

  // Scroll the modal body to the top so header messages / result buttons are seen.
  function scrollBodyTop() {
    var b = document.getElementById('sc-body'); if (b) b.scrollTop = 0;
  }

  // ── Images library view (stored screenshots, download + delete) ──
  function renderImages() {
    var body = document.getElementById('sc-body');
    body.innerHTML = '<div class="sc-note">Loading your images…</div>';
    api('/shots').then(function (r) {
      if (!r.ok) { body.innerHTML = '<div class="sc-warn">Could not load images.</div>'; return; }
      var shots = r.data.shots || [];
      ST.shots = shots.map(function (s) { return { id: s.id, url: s.url, stored: true }; });
      var cap = imageCap();
      var backBtn = '<button class="sc-btn ghost sc-back-btn" id="sc-img-back" style="padding:6px 12px;margin-bottom:12px">← Back</button>';
      var head = backBtn + '<div class="sc-meters"><div class="sc-meter"><b>IMAGES STORED</b><div class="v">' +
        shots.length + (cap ? ' / ' + cap : '') + '</div><div class="bar"><i style="width:' +
        (cap > 0 ? Math.min(100, Math.round(shots.length / cap * 100)) : 0) + '%"></i></div></div></div>';
      if (!shots.length) { body.innerHTML = head + '<div class="sc-note">No images stored yet. Use the New tab to capture some.</div>'; wireBack('sc-img-back'); return; }
      body.innerHTML = head + '<div class="sc-shots">' + shots.map(function (s) {
        return '<div class="sc-shot"><img src="' + (s.url || '') + '">' +
          '<div style="display:flex;gap:4px;padding:6px">' +
          '<a class="sc-btn" style="flex:1;text-align:center;padding:5px;font-size:11px" href="' + (s.url || '#') + '" target="_blank" download>Download</a>' +
          '<button class="sc-btn danger sc-img-del" data-id="' + s.id + '" style="padding:5px 8px;font-size:11px">Delete</button>' +
          '</div></div>';
      }).join('') + '</div>';
      wireBack('sc-img-back');
      body.querySelectorAll('.sc-img-del').forEach(function (b) {
        b.onclick = function () {
          if (!confirm('Delete this image? This frees a slot.')) return;
          api('/shots/' + b.getAttribute('data-id'), { method: 'DELETE' }).then(renderImages);
        };
      });
    });
  }

  // Wire a back button (by id) to return to the New/form view.
  function wireBack(id) {
    var b = document.getElementById(id);
    if (b) b.onclick = function () { loadConfig(); };
  }

  // ── Saved Files view (the generated-files library) ──
  function renderSaved() {
    var body = document.getElementById('sc-body');
    body.innerHTML = '<div class="sc-note">Loading your saved files…</div>';
    api('/docs').then(function (r) {
      if (!r.ok) { body.innerHTML = '<div class="sc-warn">Could not load saved files.</div>'; return; }
      var docs = r.data.docs || [];
      var scap = (ST.config && ST.config.usage && ST.config.usage.saved && ST.config.usage.saved.cap) || 0;
      var backBtn = '<button class="sc-btn ghost sc-back-btn" id="sc-files-back" style="padding:6px 12px;margin-bottom:12px">← Back</button>';
      var head = backBtn + '<div class="sc-meters"><div class="sc-meter"><b>SAVED FILES</b><div class="v">' +
        docs.length + (scap ? ' / ' + scap : '') + '</div><div class="bar"><i style="width:' +
        (scap > 0 ? Math.min(100, Math.round(docs.length / scap * 100)) : 0) + '%"></i></div></div></div>';
      if (!docs.length) { body.innerHTML = head + '<div class="sc-note">No saved files yet. Generate one from the "New" tab.</div>'; wireBack('sc-files-back'); return; }
      body.innerHTML = head + docs.map(function (d) {
        var type = d.file_type_other || d.file_type || 'Document';
        var when = d.created_at ? new Date(d.created_at).toLocaleDateString() : '';
        return '<div class="sc-q" data-id="' + d.id + '"><p><b>' + esc(d.project_name) + '</b> · ' + esc(type) +
          ' · ' + (d.output_format || '').toUpperCase() + ' · ' + esc(when) +
          ' · ' + (d.shot_count || 0) + ' shots</p><div class="opts">' +
          (d.url ? '<a class="sc-btn" href="' + d.url + '" target="_blank" download>Download</a>' : '<span class="sc-note">File unavailable</span>') +
          ' <button class="sc-btn danger sc-del-doc" data-id="' + d.id + '">Delete</button></div></div>';
      }).join('');
      wireBack('sc-files-back');
      body.querySelectorAll('.sc-del-doc').forEach(function (b) {
        b.onclick = function () {
          if (!confirm('Delete this saved file? This frees a saved-files slot.')) return;
          api('/docs/' + b.getAttribute('data-id'), { method: 'DELETE' }).then(renderSaved);
        };
      });
    });
  }

  // ── public entry: attach to a button, or expose global ──
  window.EOHScreenCapture = { open: open, close: close };
  function bindButton() {
    var btn = document.getElementById('eoh-screen-capture-btn');
    if (btn && !btn._scBound) { btn._scBound = true; btn.onclick = open; }
  }
  if (document.readyState !== 'loading') bindButton();
  else document.addEventListener('DOMContentLoaded', bindButton);
})();
